import React, { FC, useCallback, useContext, useMemo } from 'react';
import { ProductPackagesFetcher } from '../../fetchers/ProductPackagesFetcher';
import { UrlQueryDataGrid } from '../UrlQueryDataGrid/UrlQueryDataGrid';
import { GridColumns, GridRowParams } from '@mui/x-data-grid';
import { getDateValueFormatter } from '../../valueFormatters/DateValueFormatter';
import { DateFormat } from '../../helpers/dateFormat';
import { useAutoFetch } from '../../contexts/Fetcher';
import { IProductPlanPackage } from '../../models/ProductPlanPackage';
import { Single } from '../../types/single';
import { useLocation } from 'react-router-dom';
import { AddProductPackageDialog } from './AddProductPackageDialog';
import { EditProductPackageDialog } from './EditProductPackageDialog';
import { useDefaultSorting } from '../../hooks/useDefaultSorting';
import { useTranslation } from 'react-i18next';
import { CollapsedContentsWithTooltip } from '../CollapsedContentsWithTooltip/CollapsedContentsWithTooltip';
import { useDialog } from '../../hooks/useDialog';
import { useCloseDialog } from '../../hooks/useCloseDialog';
import { useQueryParam } from '../../hooks/useQueryParam';
import { apiProductPackage } from '../../resources/api/apiProductPackage';
import { useResourceChecker } from '../../resources/resource';
import { queryUrl } from '../../hooks/useQueryUrl';
import { PackagesType } from '../../models/PackagesType';

interface IProps {
  packagesType: PackagesType;
}

const { useEditPackage } = apiProductPackage;

export const ProductPackages: FC<IProps> = ({ packagesType }) => {
  const { data } = useContext(ProductPackagesFetcher.Context);
  useAutoFetch(ProductPackagesFetcher);

  const { isOpen: isAdding, close: closeDialog } = useDialog('new');

  const abortEdit = useCloseDialog(void 0, ['edit']);

  const id = useQueryParam('edit');
  const { resource: resourceEdit } = useEditPackage('');
  const resourceChecker = useResourceChecker();

  const { search } = useLocation();
  useDefaultSorting('legacyId', 'asc');
  const { t } = useTranslation();

  const columns = useMemo<GridColumns>(
    () => [
      {
        field: 'legacyId',
        headerName: t('common.tableHeader.id'),
      },
      {
        field: 'name',
        headerName: t('common.tableHeader.name'),
        flex: 1,
      },
      {
        field: 'tools',
        headerName: t('adminProducts.packages.table.toolsHeader'),
        flex: 1,
        valueGetter: (params) => {
          const count = params.row.tools.reduce((memo: number, current: Single<IProductPlanPackage['tools']>) => {
            return memo + current.quantity;
          }, 0);
          return t('adminProducts.packages.table.totalTools', { count });
        },
        renderCell: (params) => {
          const items = (params.row.tools as IProductPlanPackage['tools']).map((tool) => ({
            amount: tool.quantity,
            name: tool.name,
          }));
          return <CollapsedContentsWithTooltip items={items}>{params.value}</CollapsedContentsWithTooltip>;
        },
      },
      {
        field: 'created',
        headerName: t('common.tableHeader.created'),
        width: 120,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
      {
        field: 'updated',
        headerName: t('common.tableHeader.updated'),
        width: 120,
        valueFormatter: getDateValueFormatter(DateFormat.DateMedium),
      },
    ],
    []
  );

  const rows = useMemo(() => {
    if (!data) return null;
    return data.packages
      .filter((pack) => (packagesType === PackagesType.Addons ? pack.isAddon : !pack.isAddon))
      .map((pack) => ({
        id: pack.id,
        legacyId: pack.legacyId,
        name: pack.name,
        created: pack.created,
        updated: pack.updated,
        tools: pack.tools,
      }));
  }, [data, packagesType]);

  const getRowLink = useCallback((params: GridRowParams) => queryUrl('edit', params.row.id, search), [search]);

  const isGetRowLinkAllowed = resourceChecker(resourceEdit);

  if (!data || !rows) return null;
  const pack = id ? data.packages.find((p) => p.id === id) : undefined;

  return (
    <div>
      <UrlQueryDataGrid
        columns={columns}
        rows={rows}
        disableExtendRowFullWidth={false}
        autoHeight={true}
        disableSelectionOnClick={true}
        getRowLink={isGetRowLinkAllowed ? getRowLink : void 0}
      />
      <AddProductPackageDialog open={isAdding} closeDialog={closeDialog} packagesType={packagesType} />
      <EditProductPackageDialog pack={pack} closeDialog={abortEdit} packagesType={packagesType} />
    </div>
  );
};
